import {
  Alert,
  Box,
  Button,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  athenaApi,
  HandlersDeliverableResponse,
  usePatchApiDeliverablesByIdMutation,
  usePostApiAttachmentsMutation,
  usePostApiDeliverablesByIdCancelMutation,
  usePostApiDeliverablesByIdSubmitMutation,
  usePostApiDeliverablesMutation,
} from '../../../../../app/athenaApiGen';
import {
  setCreateModal,
  setEditModalOpen,
  setPage,
  setPageSize,
  setSelectedRowId,
} from '../../../../../app/deliverables/deliverablesSlice';
import { RootState, store } from '../../../../../app/store';
import NoPermissionTooltip from '../../../../../components/Tooltips/NoPermissionTooltip';
import UploadProgressDialog from '../../../../../components/attachments/UploadProgressDialog';
import { useMultiFileUpload } from '../../../../../components/attachments/useMultiFileUpload';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../../../../utils/tableVariables';
import { isActionEnabled } from '../../../../utilities';
import { createEmptyDeliverable } from '../../../consts';
import { useDeliverableModalContext } from '../DeliverableModalContext';
import FormInputs from './FormInputs';
import ReviewStep from './ReviewStep';
import UploaderField from './UploaderField';

const steps = ['Information', 'Attachments', 'Review'];

const StepperContent = () => {
  const dispatch = useDispatch();
  const {
    deliverable,
    uploadedFiles,
    setDeliverable,
    setUploadedFiles,
    setSavedFiles,
    originalWorkflow,
  } = useDeliverableModalContext();
  const { allowedActions, isEditModalOpen, selectedRowId } = useSelector(
    (state: RootState) => state.deliverables,
  );
  const { profile } = useSelector((state: RootState) => state.user);
  const [activeStep, setActiveStep] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [submitDeliverable, { isLoading: submittingLoading }] =
    usePostApiDeliverablesByIdSubmitMutation();
  const [updateDeliverable, { isLoading: updateLoading }] =
    usePatchApiDeliverablesByIdMutation();
  const [postDeliverable, { isLoading: deliverableLoading }] =
    usePostApiDeliverablesMutation();
  const [uploadAttachment] = usePostApiAttachmentsMutation();
  const [cancelDeliverable] = usePostApiDeliverablesByIdCancelMutation();
  const upload = useMultiFileUpload();

  // If the deliverable came in from an in-review state (edit invoked via the
  // ActionsMenu "Edit" on a submitted row), return it to Pending before firing
  // the update mutation — backend rejects updates on non-Pending workflows.
  // Uses the dedicated cancel endpoint, not a raw workflow PATCH: services
  // 0.29.0 removed the reset-to-Pending transition from the PATCH validator,
  // and the cancel endpoint also resets the tasks, clears DateSubmitted and
  // writes the "Returned" audit rows. No-op for Create flow or when already
  // Pending.
  const cancelWorkflowIfNeeded = async () => {
    const wfState = originalWorkflow?.workflowState;
    if (!selectedRowId || !wfState || wfState === 'Pending') return;
    await cancelDeliverable({ id: selectedRowId });
  };
  // Fires on dialog Close so the "Upload Complete!" summary stays visible
  // until the user dismisses it.
  const pendingCloseRef = useRef<null | (() => void)>(null);

  const handleUploadDialogClose = () => {
    upload.reset();
    if (pendingCloseRef.current) {
      const run = pendingCloseRef.current;
      pendingCloseRef.current = null;
      run();
    }
  };

  const isSubmittingEnabled = isActionEnabled(
    allowedActions,
    'submit-deliverable',
  );

  const saveAttachments = async (
    deliverable: HandlersDeliverableResponse,
  ): Promise<{ success: boolean; hadUploads: boolean }> => {
    const deliverableId = isEditModalOpen
      ? selectedRowId
      : deliverable.deliverableId;
    const authorId = profile?.contact.contactId ?? '';
    if (uploadedFiles.length === 0 || !deliverableId) {
      return { success: true, hadUploads: false };
    }

    const tasks = uploadedFiles.map((file) => ({
      file,
      upload: async (signal: AbortSignal) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('objectId', deliverableId);
        formData.append('objectType', 'Deliverable');
        formData.append('name', file.name);
        formData.append('description', deliverable?.description || '');
        formData.append('creatorContactId', authorId);
        // @ts-ignore — codegen'd body type expects an object, not FormData.
        const request = uploadAttachment({ body: formData });
        signal.addEventListener('abort', () => request.abort(), {
          once: true,
        });
        return request.unwrap();
      },
    }));

    const result = await upload.start(tasks);
    return {
      success: result.phase === 'done' && result.errorCount === 0,
      hadUploads: true,
    };
  };

  const sanitizeDeliverable = <
    T extends { locationId?: string; projectId?: string },
  >(
    d: T,
  ): T => ({
    ...d,
    locationId: d.locationId || undefined,
    projectId: d.projectId || undefined,
  });

  // Returns null on upload failure so the "&Submit" caller short-circuits
  // rather than submitting an incomplete attachment set.
  const saveDeliverable = async () => {
    try {
      const newDeliverable = await postDeliverable({
        handlersDeliverableCreateRequest: sanitizeDeliverable(deliverable),
      }).unwrap();
      const { success, hadUploads } = await saveAttachments(newDeliverable);
      store.dispatch(
        athenaApi.util.invalidateTags(['Library', 'Deliverables']),
      );
      if (!success) return null;
      const closeCreateModal = () => {
        setDeliverable(createEmptyDeliverable());
        setUploadedFiles([]);
        dispatch(setCreateModal({ isOpen: false }));
        if (newDeliverable.deliverableId) {
          dispatch(setSelectedRowId(newDeliverable.deliverableId));
        }
      };
      if (hadUploads) {
        pendingCloseRef.current = closeCreateModal;
      } else {
        closeCreateModal();
      }
      return newDeliverable.deliverableId;
    } catch {
      setError('Error in Saving Deliverable, Please Try Again.');
    }
  };

  const handleNext = async () => {
    if (activeStep !== steps.length - 1) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else {
      if (!isEditModalOpen) {
        await saveDeliverable();
      } else {
        if (!selectedRowId) return;

        try {
          await cancelWorkflowIfNeeded();
          await updateDeliverable({
            id: selectedRowId,
            handlersDeliverableUpdateRequest: sanitizeDeliverable(deliverable),
          }).unwrap();
          const { success, hadUploads } = await saveAttachments(deliverable);
          setError('');
          dispatch(setPage(0));
          dispatch(setPageSize(DEFAULT_TABLE_PAGE_SIZE));
          store.dispatch(athenaApi.util.invalidateTags(['Library']));
          if (success) {
            const closeEditModal = () => {
              setDeliverable(createEmptyDeliverable());
              setUploadedFiles([]);
              setSavedFiles([]);
              dispatch(setEditModalOpen(false));
            };
            if (hadUploads) {
              pendingCloseRef.current = closeEditModal;
            } else {
              closeEditModal();
            }
          }
        } catch {
          setError('Error in Editing Deliverable, Please Try Again.');
        }
      }
    }
  };

  const handleNextSubmit = async () => {
    if (activeStep !== steps.length - 1) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else {
      if (!isEditModalOpen) {
        saveDeliverable()
          .then(async (id) => {
            if (id) {
              try {
                await submitDeliverable({ id });
                store.dispatch(
                  athenaApi.util.invalidateTags([
                    'Approval Queue',
                    'Deliverables',
                  ]),
                );
                setError('');
                // Modal close is owned by saveDeliverable.
              } catch {
                setError('Could not Submit Deliverable');
              }
            }
          })
          .catch(() => {
            setError('Could not Submit Deliverable');
          });
      } else {
        if (!selectedRowId) return;

        try {
          await cancelWorkflowIfNeeded();
          await updateDeliverable({
            id: selectedRowId,
            handlersDeliverableUpdateRequest: sanitizeDeliverable(deliverable),
          }).unwrap();
          const { success, hadUploads } = await saveAttachments(deliverable);
          if (!success) {
            // Don't submit an incomplete attachment set for approval.
            store.dispatch(athenaApi.util.invalidateTags(['Library']));
            return;
          }
          await submitDeliverable({ id: selectedRowId });
          setError('');
          dispatch(setPage(0));
          dispatch(setPageSize(DEFAULT_TABLE_PAGE_SIZE));
          store.dispatch(
            athenaApi.util.invalidateTags(['Library', 'Approval Queue']),
          );

          const closeEditModal = () => {
            setDeliverable(createEmptyDeliverable());
            setUploadedFiles([]);
            setSavedFiles([]);
            dispatch(setEditModalOpen(false));
          };
          if (hadUploads) {
            pendingCloseRef.current = closeEditModal;
          } else {
            closeEditModal();
          }
        } catch {
          setError('Error in Submitting Deliverable, Please Try Again.');
        }
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const disableNext =
    activeStep === 0
      ? deliverable.deliverableTitle?.length === 0 ||
        !deliverable.deliverableTypeId ||
        !deliverable.deliverableTitle ||
        !deliverable.workflowType ||
        deliverable.workflowType?.length === 0 ||
        deliverable.deliverableTypeId?.length === 0
      : activeStep === 1
        ? uploadedFiles?.length === 0 && !isEditModalOpen
        : false;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <Stepper activeStep={activeStep}>
        {steps.map((label, index) => {
          const stepProps: { completed?: boolean } = {};
          return (
            <Step key={label} {...stepProps}>
              <StepLabel>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      {activeStep === steps.length - 1 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '97%',
            padding: 2,
          }}
        >
          <ReviewStep />
          {deliverableLoading ||
            submittingLoading ||
            (updateLoading && (
              <Alert
                severity="info"
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  '.MuiAlert-message': {
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%',
                    gap: 2,
                  },
                }}
              >
                {' '}
                {deliverableLoading ? (
                  <>
                    <Typography variant="subtitle2">Saving...</Typography>
                  </>
                ) : submittingLoading ? (
                  <>
                    <Typography variant="subtitle2">Submitting...</Typography>
                  </>
                ) : updateLoading ? (
                  <>
                    <Typography variant="subtitle2">Updating...</Typography>
                  </>
                ) : (
                  ''
                )}
              </Alert>
            ))}
          {error && (
            <Alert severity="error">
              <Typography>{error}</Typography>
            </Alert>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              pt: 2,
              gap: 1,
            }}
          >
            <Button
              color="inherit"
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Back
            </Button>
            <Box sx={{ flex: '1 1 auto' }} />
            <Button variant="outlined" onClick={handleNext}>
              {isEditModalOpen ? 'Update' : 'Save'}
            </Button>
            <NoPermissionTooltip isDisabled={!!!isSubmittingEnabled}>
              <Button
                disabled={!isSubmittingEnabled}
                variant="contained"
                onClick={handleNextSubmit}
              >
                {`${isEditModalOpen ? 'Update' : 'Save'} & Submit`}
              </Button>
            </NoPermissionTooltip>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '97%',
            padding: 2,
          }}
        >
          {activeStep === 0 ? <FormInputs /> : <UploaderField />}

          <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
            <Button
              color="inherit"
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
            >
              Back
            </Button>
            <Box sx={{ flex: '1 1 auto' }} />
            <Button onClick={handleNext} disabled={disableNext}>
              {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
            </Button>
          </Box>
        </Box>
      )}

      <UploadProgressDialog
        open={upload.state.phase !== 'idle'}
        state={upload.state}
        onCancel={upload.cancel}
        onClose={handleUploadDialogClose}
      />
    </Box>
  );
};

export default StepperContent;
